import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, CheckCircle2, Landmark, ShieldCheck, Wand2 } from 'lucide-react';
import { useI18n, useTranslation } from '@solara/i18n';
import { useAuthorizeEmaraTax } from '@solara/api-client/react';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Input,
  Select,
  Stepper,
  cn,
} from '@solara/ui-shared';
import {
  ErpTypeSchema,
  IntegrationModeSchema,
  MfaMethodSchema,
  TenantSchema,
} from '@solara/domain';
import { generateOnboardingFormSample } from '@solara/sample-data';

type StepKey = 'company' | 'verification' | 'setup';

interface FormState {
  companyNameEn: string;
  companyNameAr: string;
  trn: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  mfaMethod: 'TOTP' | 'SMS' | 'Email' | 'UAE_PASS';
  erpType: 'SAP' | 'Oracle' | 'Dynamics365' | 'Zoho' | 'NetSuite' | 'Tally' | 'Other';
  integrationMode: 'API' | 'Portal' | 'FileUpload';
}

const initialState: FormState = {
  companyNameEn: '',
  companyNameAr: '',
  trn: '',
  contactName: '',
  contactEmail: '',
  contactPhone: '',
  mfaMethod: 'TOTP',
  erpType: 'Other',
  integrationMode: 'Portal',
};

export default function OnboardingPage() {
  const { t } = useTranslation('onboarding');
  const { t: tP1 } = useTranslation('p1');
  const { direction } = useI18n();
  const navigate = useNavigate();

  const [stepIdx, setStepIdx] = useState(0);
  const [form, setForm] = useState<FormState>(initialState);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [done, setDone] = useState(false);

  const steps = useMemo(
    () => [
      {
        key: 'company',
        label: t('steps.company.short'),
        description: t('steps.company.description'),
      },
      {
        key: 'verification',
        label: t('steps.verification.short'),
        description: t('steps.verification.description'),
      },
      {
        key: 'setup',
        label: t('steps.setup.short'),
        description: t('steps.setup.description'),
      },
    ],
    [t],
  );

  const stepKey: StepKey = (['company', 'verification', 'setup'] as const)[stepIdx]!;

  function setField<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((s) => ({ ...s, [key]: value }));
    setErrors((e) => {
      const next = { ...e };
      delete next[key];
      return next;
    });
  }

  function fillWithSample() {
    setForm(generateOnboardingFormSample());
    setErrors({});
  }

  function validateCompanyStep(): boolean {
    const next: typeof errors = {};
    if (!form.companyNameEn.trim()) next.companyNameEn = 'REQUIRED';
    if (!form.companyNameAr.trim()) next.companyNameAr = 'REQUIRED';
    if (!/^1\d{14}$/.test(form.trn)) next.trn = 'E001';
    if (!form.contactName.trim()) next.contactName = 'REQUIRED';
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.contactEmail)) next.contactEmail = 'E010';
    if (!/^\+[1-9]\d{7,14}$/.test(form.contactPhone)) next.contactPhone = 'E011';
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  async function handleFinish() {
    // Mock the would-be POST /v1/tenants — we just validate the assembled
    // shape against the Zod schema and pretend we succeeded.
    const candidate = {
      tenantId: '00000000-0000-0000-0000-000000000000',
      companyNameEn: form.companyNameEn,
      companyNameAr: form.companyNameAr,
      trn: form.trn,
      contactName: form.contactName,
      contactEmail: form.contactEmail,
      contactPhone: form.contactPhone,
      erpType: form.erpType,
      integrationMode: form.integrationMode,
      mfaMethod: form.mfaMethod,
      onboardingStatus: 'PENDING' as const,
      createdAt: new Date().toISOString(),
      activatedAt: null,
    };
    const parsed = TenantSchema.safeParse(candidate);
    if (!parsed.success) {
      // Surface aggregated errors in the console; in a real app these
      // would map to field-level errors.
      // eslint-disable-next-line no-console
      console.error('TenantSchema rejected payload', parsed.error.format());
    }
    setDone(true);
  }

  if (done) {
    return <FinishedPanel onDashboard={() => navigate('/')} />;
  }

  return (
    <div className="space-y-6 max-w-3xl mx-auto" dir={direction}>
      <header className="text-center space-y-2">
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">{t('title')}</h1>
        <p className="text-fg-muted text-sm">{t('subtitle')}</p>
        <div className="pt-2">
          <Button
            variant="outline"
            size="sm"
            onClick={fillWithSample}
            className="gap-2"
            title={t('actions.fillSampleHint')}
          >
            <Wand2 className="h-4 w-4" />
            {t('actions.fillSample')}
          </Button>
        </div>
      </header>

      <div className="px-2">
        <Stepper steps={steps} current={stepIdx} onStepClick={(i) => i < stepIdx && setStepIdx(i)} />
      </div>

      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle>
            {stepKey === 'company' && t('company.heading')}
            {stepKey === 'verification' && t('verification.heading')}
            {stepKey === 'setup' && t('setup.heading')}
          </CardTitle>
        </CardHeader>
        <CardBody className="space-y-6">
          {stepKey === 'company' && (
            <CompanyStep form={form} errors={errors} setField={setField} />
          )}
          {stepKey === 'verification' && <VerificationStep />}
          {stepKey === 'setup' && (
            <SetupStep form={form} setField={setField} />
          )}
        </CardBody>
      </Card>

      {/* M2 spec — KYC + UBO completion is required before the tenant goes live.
          These sub-flows have their own routes so they can be revisited later. */}
      <Card className="rounded-2xl border-dashed">
        <CardHeader>
          <CardTitle>{tP1('onboardingFollowUp.title')}</CardTitle>
          <p className="text-xs text-fg-muted">{tP1('onboardingFollowUp.subtitle')}</p>
        </CardHeader>
        <CardBody className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Link
            to="/onboarding/kyc"
            className="rounded-xl border border-border bg-surface px-4 py-3 text-sm hover:bg-surface-muted"
          >
            <p className="font-medium">{tP1('onboardingFollowUp.kycDocs')}</p>
            <p className="text-xs text-fg-muted">{tP1('onboardingFollowUp.kycDocsHint')}</p>
          </Link>
          <Link
            to="/onboarding/ubo"
            className="rounded-xl border border-border bg-surface px-4 py-3 text-sm hover:bg-surface-muted"
          >
            <p className="font-medium">{tP1('onboardingFollowUp.ubo')}</p>
            <p className="text-xs text-fg-muted">{tP1('onboardingFollowUp.uboHint')}</p>
          </Link>
        </CardBody>
      </Card>

      <nav className="flex items-center justify-between gap-3">
        <Button
          variant="ghost"
          onClick={() => (stepIdx === 0 ? navigate(-1) : setStepIdx((s) => s - 1))}
          className="gap-2"
        >
          <ArrowLeft className="h-4 w-4 rtl-mirror" />
          {t('actions.back')}
        </Button>
        <div className="flex items-center gap-2">
          {stepIdx < steps.length - 1 ? (
            <Button
              onClick={() => {
                if (stepKey === 'company' && !validateCompanyStep()) return;
                setStepIdx((s) => s + 1);
              }}
              className="gap-2"
            >
              {t('actions.next')}
              <ArrowRight className="h-4 w-4 rtl-mirror" />
            </Button>
          ) : (
            <Button onClick={handleFinish} className="gap-2">
              {t('actions.finish')}
              <ArrowRight className="h-4 w-4 rtl-mirror" />
            </Button>
          )}
        </div>
      </nav>
    </div>
  );
}

// --- Step components ---------------------------------------------------------

function CompanyStep({
  form,
  errors,
  setField,
}: {
  form: FormState;
  errors: Partial<Record<keyof FormState, string>>;
  setField: <K extends keyof FormState>(k: K, v: FormState[K]) => void;
}) {
  const { t } = useTranslation('onboarding');
  const { t: tErr } = useTranslation('errors');
  const errLabel = (code?: string) => {
    if (!code) return undefined;
    if (code === 'REQUIRED') return t('errors.required');
    return tErr(code, { defaultValue: code });
  };

  return (
    <div className="grid gap-4 md:grid-cols-2">
      <Input
        label={t('company.fields.nameEn')}
        placeholder={t('company.fields.nameEnPlaceholder')}
        value={form.companyNameEn}
        onChange={(e) => setField('companyNameEn', e.target.value)}
        error={errLabel(errors.companyNameEn)}
        dir="ltr"
        required
      />
      <Input
        label={t('company.fields.nameAr')}
        placeholder={t('company.fields.nameArPlaceholder')}
        value={form.companyNameAr}
        onChange={(e) => setField('companyNameAr', e.target.value)}
        error={errLabel(errors.companyNameAr)}
        dir="rtl"
        lang="ar"
        required
      />
      <Input
        label={t('company.fields.trn')}
        placeholder="100000000001234"
        value={form.trn}
        onChange={(e) => setField('trn', e.target.value.replace(/\s/g, ''))}
        hint={t('company.fields.trnHint')}
        error={errLabel(errors.trn)}
        inputMode="numeric"
        maxLength={15}
        className="num"
        dir="ltr"
        required
      />
      <Input
        label={t('company.fields.contactName')}
        placeholder={t('company.fields.contactNamePlaceholder')}
        value={form.contactName}
        onChange={(e) => setField('contactName', e.target.value)}
        error={errLabel(errors.contactName)}
        required
      />
      <Input
        label={t('company.fields.contactEmail')}
        placeholder={t('company.fields.contactEmailPlaceholder')}
        type="email"
        value={form.contactEmail}
        onChange={(e) => setField('contactEmail', e.target.value)}
        error={errLabel(errors.contactEmail)}
        dir="ltr"
        required
      />
      <Input
        label={t('company.fields.contactPhone')}
        placeholder={t('company.fields.contactPhonePlaceholder')}
        type="tel"
        value={form.contactPhone}
        onChange={(e) => setField('contactPhone', e.target.value)}
        error={errLabel(errors.contactPhone)}
        dir="ltr"
        required
      />
    </div>
  );
}

function VerificationStep() {
  const { t } = useTranslation('onboarding');
  const authorize = useAuthorizeEmaraTax();
  const [error, setError] = useState<string | null>(null);

  async function openEmaraTax() {
    setError(null);
    try {
      const base = typeof window !== 'undefined' ? window.location.origin : undefined;
      const res = await authorize.mutateAsync(base);
      // Outbound channel: hand off to the FTA EmaraTax portal. It redirects
      // back to our callback (handled by EmaraTaxCallbackPage) with the code.
      window.location.href = res.authorizationUrl;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not start EmaraTax authorization.');
    }
  }

  return (
    <div className="space-y-5">
      <p className="text-fg-muted">{t('verification.intro')}</p>
      <div className="rounded-xl border border-brand-100 bg-brand-50 p-4 flex gap-3 items-start">
        <ShieldCheck className="h-5 w-5 text-brand-700 mt-0.5 shrink-0" />
        <p className="text-sm text-brand-800">{t('verification.trustedConnection')}</p>
      </div>

      {/* The FTA recognises TWO authorization channels. Both are audited and
          converge on the same authorization-code exchange. */}
      <div className="grid gap-4 md:grid-cols-2">
        <div className="rounded-xl border border-border p-4 space-y-2">
          <div className="flex items-center gap-2">
            <ArrowRight className="h-4 w-4 text-brand-600 rtl-mirror" />
            <h3 className="font-medium text-sm">Outbound — start here</h3>
          </div>
          <p className="text-xs text-fg-muted">
            You are redirected to the FTA EmaraTax portal to authorize Solara as your
            Accredited Service Provider, then sent back automatically.
          </p>
          <Button onClick={openEmaraTax} disabled={authorize.isPending}>
            {authorize.isPending ? 'Redirecting…' : t('verification.openEmaraTax')}
          </Button>
        </div>

        <div className="rounded-xl border border-border p-4 space-y-2">
          <div className="flex items-center gap-2">
            <Landmark className="h-4 w-4 text-brand-600" />
            <h3 className="font-medium text-sm">Inbound — started on EmaraTax</h3>
          </div>
          <p className="text-xs text-fg-muted">
            If you selected Solara as your ASP directly on the EmaraTax portal, the FTA
            redirects you here with an authorization code — no action needed; the
            verification completes automatically on arrival.
          </p>
          <Badge tone="neutral">FTA-controlled redirect</Badge>
        </div>
      </div>

      {error ? <p className="text-sm text-danger">{error}</p> : null}

      <div className="flex flex-wrap gap-3">
        <Button variant="outline">{t('verification.skipForDemo')}</Button>
      </div>
    </div>
  );
}

function SetupStep({
  form,
  setField,
}: {
  form: FormState;
  setField: <K extends keyof FormState>(k: K, v: FormState[K]) => void;
}) {
  const { t } = useTranslation('onboarding');

  const mfaOptions = MfaMethodSchema.options.map((value) => ({
    value,
    label: t(`setup.options.mfa.${value}`),
  }));
  const erpOptions = ErpTypeSchema.options.map((value) => ({
    value,
    label: t(`setup.options.erp.${value}`),
  }));

  return (
    <div className="space-y-6">
      <p className="text-fg-muted">{t('setup.intro')}</p>
      <div className="grid gap-4 md:grid-cols-2">
        <Select
          label={t('setup.fields.mfa')}
          options={mfaOptions}
          value={form.mfaMethod}
          onChange={(e) => setField('mfaMethod', e.target.value as FormState['mfaMethod'])}
          hint={t('setup.fields.mfaHint')}
          required
        />
        <Select
          label={t('setup.fields.erp')}
          options={erpOptions}
          value={form.erpType}
          onChange={(e) => setField('erpType', e.target.value as FormState['erpType'])}
          hint={t('setup.fields.erpHint')}
        />
      </div>

      {form.mfaMethod === 'TOTP' ? <TotpSetup /> : null}

      <fieldset>
        <legend className="text-sm font-medium text-fg mb-2">
          {t('setup.fields.integration')}
        </legend>
        <div className="grid gap-3 sm:grid-cols-3">
          {IntegrationModeSchema.options.map((mode) => (
            <button
              key={mode}
              type="button"
              onClick={() => setField('integrationMode', mode)}
              className={cn(
                'rounded-xl border p-4 text-start transition-all',
                form.integrationMode === mode
                  ? 'border-brand-600 bg-brand-50 ring-2 ring-brand-100'
                  : 'border-border hover:border-border-strong',
              )}
            >
              <div className="font-medium">{t(`setup.options.integration.${mode}`)}</div>
            </button>
          ))}
        </div>
      </fieldset>
    </div>
  );
}

function TotpSetup() {
  const { t } = useTranslation('onboarding');
  // Stable mock secret + issuer — generated once per mount.
  const secret = 'JBSWY3DPEHPK3PXP';
  const otpAuthUrl = `otpauth://totp/Solara:demo@dubaitrading.ae?secret=${secret}&issuer=Solara%20Platform&algorithm=SHA1&digits=6&period=30`;
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=180x180&margin=8&data=${encodeURIComponent(otpAuthUrl)}`;
  const [code, setCode] = useState('');
  const [verified, setVerified] = useState(false);
  const validCode = /^\d{6}$/.test(code);

  return (
    <div className="rounded-2xl border border-brand-100 bg-brand-50/40 p-4 sm:p-5">
      <div className="flex flex-col sm:flex-row gap-5 sm:items-start">
        <img
          src={qrSrc}
          alt="TOTP QR code"
          width={180}
          height={180}
          className="rounded-lg bg-white p-2 shadow-sm shrink-0 mx-auto sm:mx-0"
        />
        <div className="flex-1 min-w-0 space-y-3">
          <div>
            <h3 className="text-sm font-semibold tracking-tight">{t('totp.title')}</h3>
            <p className="text-sm text-fg-muted mt-1">{t('totp.subtitle')}</p>
          </div>
          <div className="text-xs space-y-1">
            <div className="text-fg-subtle">{t('totp.secretLabel')}</div>
            <code className="block rounded bg-white border border-border px-2 py-1 font-mono break-all" dir="ltr">
              {secret}
            </code>
          </div>
          {verified ? (
            <div className="rounded-lg border border-success/30 bg-success/5 p-3 flex items-start gap-2">
              <CheckCircle2 className="h-5 w-5 text-success shrink-0 mt-0.5" />
              <div className="text-sm">
                <div className="font-medium">{t('totp.verified')}</div>
                <div className="text-fg-muted">{t('totp.verifiedBody')}</div>
              </div>
            </div>
          ) : (
            <div className="grid gap-2 sm:grid-cols-[1fr_auto] sm:items-end">
              <Input
                label={t('totp.codeLabel')}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder={t('totp.codePlaceholder')}
                inputMode="numeric"
                maxLength={6}
                className="num tracking-widest"
                dir="ltr"
                error={code.length === 6 && !validCode ? t('errors.totpInvalid') : undefined}
              />
              <Button
                type="button"
                disabled={!validCode}
                onClick={() => setVerified(true)}
              >
                {t('totp.verify')}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function FinishedPanel({ onDashboard }: { onDashboard: () => void }) {
  const { t } = useTranslation('onboarding');
  const { t: tP1 } = useTranslation('p1');
  return (
    <div className="max-w-xl mx-auto text-center py-12">
      <div className="mx-auto h-16 w-16 rounded-full bg-success/15 grid place-items-center text-success">
        <CheckCircle2 className="h-9 w-9" />
      </div>
      <h2 className="mt-6 text-2xl font-semibold tracking-tight">{t('summary.heading')}</h2>
      <p className="mt-2 text-fg-muted">{t('summary.subtitle')}</p>
      <div className="mt-6 flex items-center justify-center gap-3">
        <Badge tone="warning">{tP1('onboardingFollowUp.pending')}</Badge>
        <Badge tone="info">{tP1('onboardingFollowUp.peppolInProgress')}</Badge>
      </div>
      <div className="mt-8">
        <Button onClick={onDashboard} className="gap-2">
          {t('summary.openDashboard')}
          <ArrowRight className="h-4 w-4 rtl-mirror" />
        </Button>
      </div>
      <p className="mt-6 text-xs text-fg-subtle">
        <Link to="/" className="underline">
          {tP1('onboardingFollowUp.backToPortal')}
        </Link>
      </p>
    </div>
  );
}
